<template>
    <div class="">
        <install-wizard-card>
            <template #top-header>
                Point Setup
            </template>
            <template #top-content>
                Let's name your points!
            </template>

            <template #body-summary>
                In SoniCloud, a <i>point</i> is a single unit of monitoring. This step will help you define
                what to name these points and what Zones you would like them to be placed into.
                <br/><br/>
                It's usually good to incorporate unique or identifying information in the name, as this name
                is what will be presented to you during Alarms.
                <br/><br/>
                <p class="text-sm text-gray-600">ex. 'Freezer' may not be identifying. Try 'Pharmacy Freezer #3 (Vaccines)' instead.</p>


                <div class="mt-4 w-full md:w-1/3">
                    <floating-input
                            id="search"
                            label="Search By Serial Number"
                            v-model:value="search"
                    ></floating-input>
                </div>
            </template>

            <template #body-action>
                <install-wizard-well    v-for="(device, index) in physicalDevicesList" :key="index"
                                        v-show="shouldShow(index)"
                                        :collapsed-bg="isEven(index) ? 'bg-white' : 'bg-gray-100'"
                                        :expanded="deviceHasUnsavedPoints(device)">
                    <template #well-header-left>
                        <div class="text-sm font-medium leading-5 text-blue-600 truncate">{{index}}</div>
                        <div class="flex flex-wrap items-center mt-2 text-sm leading-5 text-gray-500">
                            <div v-for="point in device"
                                class="w-full">
                                <span :class="needsSaved(point.meter.ID) ? 'icon-sync-crossed' : 'icon-check'" class="w-2 lnr"></span>
                                {{getMeterName(point)}}
                            </div>
                        </div>
                    </template>
                    <template #well-header-center>
                        <div>
                            <div class="text-sm leading-5 text-gray-900">
                                {{getMeterType(device)}}
                            </div>
                            <!--                                        <div class="flex items-center mt-2 text-sm leading-5 text-gray-500">-->
                            <!--                                            Pharmacy-->
                            <!--                                        </div>-->
                        </div>
                    </template>
                    <template #well-content>
                        <ul class="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-2">
                            <install-wizard-electronic-install-card
                                    v-for="point in device"
                                    :key="point.ID"
                                    :point="point"
                                    :area-zones="areaZones"
                                    v-on:saved="deviceWasSaved"
                                    v-on:needs-saved="deviceNeedsSaved"
                            ></install-wizard-electronic-install-card>
                        </ul>
                    </template>
                </install-wizard-well>
            </template>

            <template #bottom-header>
                Got everything named?
            </template>
            <template #bottom-button>
                <a>
                    <button v-if="allDevicesSaved"
                            type="submit"
                            @click="completeStep"
                            :disabled="!allDevicesSaved"
                            class="relative inline-flex items-center px-4 py-2 text-sm font-medium leading-5 text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-500 focus:outline-none focus:ring-blue-200 focus:border-blue-700 active:bg-blue-700">
                        <slot name="bottom-button-text">Save and continue!</slot>
                    </button>
                    <button v-else
                            type="submit"
                            @click="completeStep"
                            :disabled="!allDevicesSaved"
                            class="relative inline-flex items-center px-4 py-2 font-sans leading-5 bg-gray-300 border border-blue-200 rounded-md text-md font-sm text-white-700 hover:bg-gray-300 focus:outline-none focus:ring-blue-200 focus:border-blue-400">
                        <slot name="bottom-button-text">Set up all points to continue!</slot>
                    </button>
                </a>
            </template>
        </install-wizard-card>
        <install-wizard-well></install-wizard-well>
    </div>
</template>

<script>
    import InstallWizardCard from "./InstallWizardCard";
    import InstallWizardWell from "@/components/InstallWizard/InstallWizardWell";
    import InstallWizardElectronicInstallCard from "@/components/InstallWizard/InstallWizardElectronicInstallCard";
    import axios from "axios";
    import FloatingInput from "../elements/FloatingInput";

    export default {
        name: "InstallWizardElectronicInstall",
        props: {
            physicalDevicesList: Object,
            areaZones: Object,
        },
        data(){
            return {
                savedPoints: [],
                search: '',
            }
        },
        components: {FloatingInput, InstallWizardElectronicInstallCard, InstallWizardWell, InstallWizardCard},
        methods: {
            shouldShow(deviceSerial) {
                if (_.isNil(deviceSerial)) {
                    return false
                }

                let search = this.search.toLowerCase()
                return deviceSerial.toLowerCase().indexOf(search) !== -1
            },
            isEven(serial) {
                let found = false
                let countToIndex = -1
                _.forEach(this.physicalDevicesList, (device, index) => {
                    if (found) {
                        return
                    }

                    countToIndex++
                    if (index === serial) {
                        found = true
                    }
                })
                return countToIndex % 2
            },
            completeStep() {
                axios.post(window.location.href, {}).then(
                    response => {
                        window.location.href = response.data.target;
                    }
                ).catch(error => {
                    console.log("Error: " + error)
                })
            },
            needsSaved(pointId){
                return _.indexOf(this.savedPoints, pointId) === -1;
            },
            deviceHasUnsavedPoints(device) {
                let unsaved = false
                _.forEach(device, meter => {
                    if (_.indexOf(this.savedPoints, meter.ID) === -1) {
                        unsaved = true
                    }
                })
                return unsaved
            },
            deviceWasSaved(device){
                this.savedPoints.push(device.meter.ID);
            },
            deviceNeedsSaved(device){
                this.savedPoints = _.without(this.savedPoints, device.meter.ID);
            },
            getMeterName(device){
                if(device.meter.is_default_name){
                    return 'Point needs name!';
                } else {
                    return device.meter.Name;
                }
            },
            getMeterType(device){
                // console.log(device);
                if(device.length > 0){
                    if(device[0].settingsArray != null && device[0].settingsArray.device_type != null){
                        return device[0].settingsArray.device_type;
                    }
                }
                return '';
            }
        },
        computed: {
            allDevicesSaved(){
                let numberOfPoints = 0;
                _.forOwn(this.physicalDevicesList, (devices, key) => {
                    numberOfPoints += devices.length;
                })
                return numberOfPoints === this.savedPoints.length;
            },
        }
    }
</script>

<style scoped>

</style>